import React, { useRef, useEffect, useCallback } from "react";
import { Box, Button, CircularProgress, Typography, IconButton, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { IApplicationState } from "@/store-redux";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { motion } from "framer-motion";
import { MagicCard, BorderBeam, ShimmerButton, VoiceVisualizerOrb } from "@/components/MagicUI";
import LucianPlayAudio from "@/components/LucianUI/LucianPlayAudio";

interface WaveformRecorderProps {
  isRecording: boolean;
  recordingTime: number;
  audioUrl: string | null;
  isAnalyzing: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onAnalyze?: () => void;
  onReset: () => void;
  maxRecordingTime?: number;
  subtitleText?: string;
  disableAnalyze?: boolean;
}

const convertSecondToTime = (sec: number) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

export default function WaveformRecorder({
  isRecording,
  recordingTime,
  audioUrl,
  isAnalyzing,
  onStartRecording,
  onStopRecording,
  onAnalyze,
  onReset,
  maxRecordingTime = 120,
  disableAnalyze = false,
}: WaveformRecorderProps) {
  const appMode = useSelector((state: IApplicationState) => state.appMode);
  const isDark = appMode === "dark";

  // Sound Visualizer refs
  const visualizerRef = useRef<HTMLCanvasElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const visualizerFrameRef = useRef<number | null>(null);
  const waveHistoryRef = useRef<number[]>([]);

  const stopVisualizer = useCallback(() => {
    if (visualizerFrameRef.current) {
      cancelAnimationFrame(visualizerFrameRef.current);
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t: MediaStreamTrack) => t.stop());
    }
    streamRef.current = null;
    audioCtxRef.current = null;
    sourceRef.current = null;
    analyserRef.current = null;
    waveHistoryRef.current = [];
  }, []);

  const drawVisualizer = useCallback(() => {
    const canvas = visualizerRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const analyser = analyserRef.current;
    if (!analyser) return;

    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    const maxSamples = 240;
    let lastSampleTime = 0;

    const draw = (timestamp: number) => {
      visualizerFrameRef.current = requestAnimationFrame(draw);

      if (timestamp - lastSampleTime > 75) {
        lastSampleTime = timestamp;

        analyser.getByteTimeDomainData(dataArray);
        let maxDev = 0;
        for (let i = 0; i < bufferLength; i++) {
          const dev = Math.abs(dataArray[i] - 128);
          if (dev > maxDev) maxDev = dev;
        }

        let amp = maxDev / 128;
        if (amp < 0.03) amp = 0;

        waveHistoryRef.current.push(amp);
        if (waveHistoryRef.current.length > maxSamples) {
          waveHistoryRef.current.shift();
        }
      }

      const width = canvas.width;
      const height = canvas.height;
      const centerY = height / 2;

      ctx.clearRect(0, 0, width, height);

      const stepWidth = width / maxSamples;
      const history = waveHistoryRef.current;

      // Center baseline
      ctx.beginPath();
      ctx.strokeStyle = isDark ? "rgba(45, 212, 191, 0.2)" : "rgba(16, 185, 129, 0.2)";
      ctx.lineWidth = 1.5;
      ctx.moveTo(0, centerY);
      ctx.lineTo(width, centerY);
      ctx.stroke();

      if (history.length === 0) return;

      // Glow setting for neon feel
      ctx.shadowBlur = 8;
      ctx.shadowColor = "#10B981";

      for (let i = 0; i < history.length; i++) {
        const x = i * stepWidth;
        const amp = history[i];

        if (amp === 0) {
          ctx.beginPath();
          ctx.strokeStyle = "#10B981";
          ctx.lineWidth = 1.8;
          ctx.moveTo(x, centerY);
          ctx.lineTo(x + stepWidth, centerY);
          ctx.stroke();
        } else {
          const barHeight = Math.max(4, amp * (height * 0.85));
          const yTop = centerY - barHeight / 2;
          const yBottom = centerY + barHeight / 2;

          ctx.beginPath();
          ctx.strokeStyle = "#10B981";
          ctx.lineWidth = Math.max(1.8, stepWidth - 0.8);
          ctx.lineCap = "round";
          ctx.moveTo(x + stepWidth / 2, yTop);
          ctx.lineTo(x + stepWidth / 2, yBottom);
          ctx.stroke();
        }
      }

      const currentX = (history.length - 1) * stepWidth;
      ctx.beginPath();
      ctx.strokeStyle = "#38BDF8";
      ctx.lineWidth = 2.5;
      ctx.moveTo(currentX + stepWidth / 2, centerY - height * 0.45);
      ctx.lineTo(currentX + stepWidth / 2, centerY + height * 0.45);
      ctx.stroke();
    };

    visualizerFrameRef.current = requestAnimationFrame(draw);
  }, [isDark]);

  const startVisualizer = useCallback(async () => {
    try {
      waveHistoryRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioContextClass();
      audioCtxRef.current = ctx;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyserRef.current = analyser;

      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      sourceRef.current = source;

      drawVisualizer();
    } catch (err) {
      console.error("Visualizer setup failed:", err);
    }
  }, [drawVisualizer]);

  useEffect(() => {
    if (isRecording) {
      startVisualizer();
    } else {
      stopVisualizer();
    }
    return () => stopVisualizer();
  }, [isRecording, startVisualizer, stopVisualizer]);

  return (
    <MagicCard
      glowColor={isRecording ? "rgba(16, 185, 129, 0.25)" : undefined}
      sx={{
        p: 2.5,
        borderRadius: "24px",
        background: isDark
          ? "linear-gradient(145deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.75) 100%)"
          : "linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)",
        border: `1px solid ${
          isRecording ? "rgba(16, 185, 129, 0.4)" : isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(226, 232, 240, 0.9)"
        }`,
        boxShadow: isDark ? "0 8px 30px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.04)",
      }}
    >
      {/* Header bar */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            fontSize: "13.5px",
            color: isDark ? "#F8FAFC" : "#0F172A",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 16, color: "#10B981" }} /> Phòng Thu Âm & Luyện Phát Âm
        </Typography>

        {isRecording && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.25,
              borderRadius: "100px",
              bgcolor: "rgba(239, 68, 68, 0.15)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#EF4444",
                boxShadow: "0 0 8px #EF4444",
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, color: "#EF4444", fontVariantNumeric: "tabular-nums" }}
            >
              {convertSecondToTime(recordingTime)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* 1. Idle state */}
      {!isRecording && !audioUrl && !isAnalyzing && (
        <Box
          onClick={onStartRecording}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2.5,
            p: 2,
            borderRadius: "18px",
            bgcolor: isDark ? "rgba(0,0,0,0.25)" : "#F8FAFC",
            border: `1px solid ${isDark ? "rgba(16, 185, 129, 0.25)" : "rgba(16, 185, 129, 0.35)"}`,
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)",
            "&:hover": {
              bgcolor: isDark ? "rgba(16, 185, 129, 0.08)" : "rgba(16, 185, 129, 0.05)",
              borderColor: "#10B981",
              boxShadow: "0 4px 20px rgba(16, 185, 129, 0.15)",
              transform: "translateY(-1px)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <VoiceVisualizerOrb isRecording={false} size={54} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: isDark ? "#FFFFFF" : "#0F172A",
                  fontSize: "14.5px",
                }}
              >
                Nhấn quả cầu để bắt đầu thu âm
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.2,
                  borderRadius: "100px",
                  bgcolor: isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7",
                  color: isDark ? "#34D399" : "#047857",
                  fontSize: "10.5px",
                  fontWeight: 800,
                }}
              >
                <MicIcon sx={{ fontSize: 13 }} />
                Sẵn sàng
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: isDark ? "#94A3B8" : "#64748B",
                fontSize: "12px",
                display: "block",
                lineHeight: 1.45,
              }}
            >
              Hãy đọc to, rõ ràng theo đúng trọng âm và ngữ điệu câu. Hệ thống sẽ lắng nghe và phân tích từng âm vị.
            </Typography>
          </Box>
        </Box>
      )}

      {/* 2. Recording state */}
      {isRecording && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: 2,
            p: 2,
            borderRadius: "18px",
            bgcolor: isDark ? "rgba(8, 14, 26, 0.7)" : "#F8FAFC",
            border: "1px solid rgba(16, 185, 129, 0.3)",
          }}
        >
          <Box onClick={onStopRecording} sx={{ cursor: "pointer", flexShrink: 0, position: "relative" }}>
            <VoiceVisualizerOrb isRecording={true} size={58} />
            <IconButton
              size="small"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "rgba(239, 68, 68, 0.9)",
                color: "#fff",
                "&:hover": { bgcolor: "#EF4444" },
              }}
            >
              <StopIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              width: "100%",
              height: 54,
              borderRadius: "14px",
              overflow: "hidden",
              bgcolor: isDark ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.02)",
            }}
          >
            <canvas
              ref={visualizerRef}
              width={600}
              height={54}
              style={{ width: "100%", height: "100%", display: "block" }}
            />
          </Box>

          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              fontSize: "11px",
              color: recordingTime > maxRecordingTime - 20 ? "#EF4444" : "#10B981",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {recordingTime > maxRecordingTime - 20
              ? `Sắp hết giờ! (${maxRecordingTime - recordingTime}s)`
              : "🎙️ Đang lắng nghe..."}
          </Typography>
        </Box>
      )}

      {/* 3. Analyzing state */}
      {isAnalyzing && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, py: 3 }}>
          <CircularProgress size={28} sx={{ color: "#10B981" }} />
          <Typography variant="body2" sx={{ fontWeight: 800, color: "#10B981", fontSize: "14px" }}>
            Hệ thống đang chấm điểm phát âm & ngữ điệu... ✨
          </Typography>
        </Box>
      )}

      {/* 4. Review / Completed state */}
      {!isRecording && !isAnalyzing && audioUrl && (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", gap: 2 }}>
          <Box sx={{ flex: 1, width: "100%" }}>
            <LucianPlayAudio variant="bar" src={audioUrl} />
          </Box>
          <Stack direction="row" spacing={1.5} flexShrink={0}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={onReset}
              sx={{
                borderRadius: "100px",
                fontWeight: 700,
                px: 2,
                textTransform: "none",
                height: 44,
                borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
                color: isDark ? "#FFFFFF" : "#0F172A",
              }}
            >
              Thu âm lại
            </Button>
            {onAnalyze && (
              <ShimmerButton onClick={onAnalyze} disabled={disableAnalyze} sx={{ height: 44, px: 3, fontSize: "13px" }}>
                <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                Phân Tích & Chấm Điểm ✨
              </ShimmerButton>
            )}
          </Stack>
        </Box>
      )}
    </MagicCard>
  );
}
